import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { useWindowLocation } from 'utils/windowLocation'

import { useEmployerRenewalConfig } from '../providers/EmployerRenewalConfigProvider'
import { EmployerRenewalsRoute } from '../routes'
import useRenewalEmployees from './useRenewalEmployees'

const DASHBOARD_ROUTE = '/'

const determinePreviousPageRoutes = (shouldShowCensusPage: boolean, shouldShowQMEPage: boolean, shouldShowDependentCoveragePage: boolean) => ({
  [EmployerRenewalsRoute.INTRO]: DASHBOARD_ROUTE,
  [EmployerRenewalsRoute.WHAT_TO_EXPECT]: EmployerRenewalsRoute.INTRO,
  [EmployerRenewalsRoute.EMPLOYEES_HEADCOUNT]: EmployerRenewalsRoute.WHAT_TO_EXPECT,
  [EmployerRenewalsRoute.EMPLOYEES_CENSUS]: EmployerRenewalsRoute.EMPLOYEES_HEADCOUNT,
  [EmployerRenewalsRoute.EMPLOYEES_ELIGIBILITY]: shouldShowCensusPage ? EmployerRenewalsRoute.EMPLOYEES_CENSUS : EmployerRenewalsRoute.EMPLOYEES_HEADCOUNT,
  [EmployerRenewalsRoute.ALLOWANCES_REVIEW]: EmployerRenewalsRoute.EMPLOYEES_ELIGIBILITY,
  [EmployerRenewalsRoute.ALLOWANCES_QME]: EmployerRenewalsRoute.ALLOWANCES_REVIEW,
  [EmployerRenewalsRoute.ALLOWANCES_DEPENDENTS_COVERAGE]: shouldShowQMEPage ? EmployerRenewalsRoute.ALLOWANCES_QME : EmployerRenewalsRoute.ALLOWANCES_REVIEW,
  [EmployerRenewalsRoute.ALLOWANCES_CONFIRM]: shouldShowDependentCoveragePage ? EmployerRenewalsRoute.ALLOWANCES_DEPENDENTS_COVERAGE : shouldShowQMEPage ? EmployerRenewalsRoute.ALLOWANCES_QME : EmployerRenewalsRoute.ALLOWANCES_REVIEW,
  [EmployerRenewalsRoute.ALLOWANCES_COMPLETE]: EmployerRenewalsRoute.ALLOWANCES_CONFIRM,
})

const IN_APP_MISSING_REQUIRED_INFO_THRESHOLD = 30

export default function useAppNavigation () {
  const navigate = useNavigate()
  const location = useLocation()
  const { assign } = useWindowLocation()

  const { employerRenewalConfig } = useEmployerRenewalConfig()
  const { numEmployeesWithMissingRequiredInfo } = useRenewalEmployees({})

  const currentRoute = useMemo(() => {
    return location.pathname as EmployerRenewalsRoute
  }, [location.pathname])

  const canGoBack = useMemo(() => {
    return location.key !== 'default'
  }, [location.key])

  const shouldShowCensusPage = numEmployeesWithMissingRequiredInfo > IN_APP_MISSING_REQUIRED_INFO_THRESHOLD

  const shouldShowQMEPage = useMemo(() => {
    return employerRenewalConfig?.current_year_quote?.allowances?.some(allowance => allowance.qme_treatment === 'disabled') ?? true
  }, [employerRenewalConfig?.current_year_quote?.allowances])

  const shouldShowDependentCoveragePage = useMemo(() => {
    return employerRenewalConfig?.current_year_quote?.allowances?.every(allowance =>
      allowance.coverage_percentile !== null &&
        allowance.spouse_contribution_percentage === 0 &&
        allowance.dependent_contribution_percentage === 0
    ) ?? false
  }, [employerRenewalConfig?.current_year_quote?.allowances])

  const previousPageRoutes = useMemo(() => {
    return determinePreviousPageRoutes(shouldShowCensusPage, shouldShowQMEPage, shouldShowDependentCoveragePage)
  }, [shouldShowCensusPage, shouldShowQMEPage, shouldShowDependentCoveragePage])

  const navigateTo = useCallback((path: EmployerRenewalsRoute, replace?: boolean) => {
    navigate(path, { replace })
  }, [navigate])

  const navigateBack = useCallback(() => {
    if (!canGoBack && previousPageRoutes[currentRoute]) {
      navigate(previousPageRoutes[currentRoute], { replace: true })
      return
    }

    navigate(-1)
  }, [navigate, canGoBack, currentRoute])

  const goToDashboard = useCallback(() => {
    assign(DASHBOARD_ROUTE)
  }, [assign])

  return {
    navigateTo,
    navigateBack,
    goToDashboard,
    shouldShowCensusPage,
    shouldShowQMEPage,
    shouldShowDependentCoveragePage,
  }
}
